import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Flex, Button, Input, Text, useToast, FormControl, FormLabel,
} from "@chakra-ui/react";
import { FaPaperPlane } from "react-icons/fa";
import { Panel } from "../../../components/shared/Section";

const API = process.env.REACT_APP_API_URL;
const RETAILER_FLAGS = ["pnp", "chk", "dis", "mak", "cli", "med", "pet"];

const Chip = ({ on, label, onClick }) => (
  <Button
    size="xs"
    variant="ghost"
    onClick={onClick}
    aria-pressed={on}
    minW="40px"
    fontWeight="600"
    color={on ? "signal.up" : "ink.400"}
    bg={on ? "signal.upSoft" : "ink.100"}
    _hover={{ bg: on ? "signal.upSoft" : "ink.200" }}
  >
    {label}
  </Button>
);

/**
 * Organisation admin's Reports screen: email a Sales Tracker for one of the
 * org's suppliers to any address. Same generation pipeline as the master batch
 * reports; the backend caps supplier and retailers to the organisation.
 */
const OrgReports = () => {
  const toast = useToast();
  const [context, setContext] = useState(null);
  const [supplier, setSupplier] = useState("");
  // multiReport orgs (Blue Mango, 2026-10-02): several / all suppliers in one batch.
  const [picked, setPicked] = useState([]);
  const multi = !!context?.multiReport;
  // reportSuppliers (2026-10-07): Blue Mango's batch list includes its Spear guests; other screens keep `suppliers`.
  const allSuppliers = context?.reportSuppliers || context?.suppliers || [];
  const allPicked = allSuppliers.length > 0 && picked.length === allSuppliers.length;
  const togglePick = (s) => setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  const [retailers, setRetailers] = useState({});
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const entitled = RETAILER_FLAGS.filter((f) => context?.org?.[f] === true);

  useEffect(() => {
    axios
      .post(`${API}/org-admin/get-context`, {})
      .then((res) => {
        const ctx = res.data?.data || null;
        setContext(ctx);
        if (ctx?.suppliers?.length === 1) setSupplier(ctx.suppliers[0]);
      })
      .catch((error) => {
        toast({ title: "Failed to load", description: error?.response?.data?.message, status: "error", duration: 5000, isClosable: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const chosen = entitled.filter((f) => retailers[f]).map((f) => f.toUpperCase());
      const body = multi ? { suppliercodes: picked, retailers: chosen, email } : { suppliercode: supplier, retailers: chosen, email };
      const res = await axios.post(`${API}/org-admin/send-report`, body);
      toast({ title: "Report on its way", description: res.data?.message, status: "success", duration: 6000, isClosable: true });
    } catch (error) {
      toast({ title: "Failed to send", description: error?.response?.data?.message, status: "error", duration: 8000, isClosable: true });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Panel maxW="560px">
        <form onSubmit={handleSend}>
          <Text fontWeight="600" mb={3}>Email a Sales Tracker report</Text>
          <FormControl isRequired mb={2}>
            <FormLabel fontSize="sm">{multi ? "Suppliers" : "Supplier"}</FormLabel>
            {multi ? (
              <Flex gap={1} wrap="wrap">
                <Chip on={allPicked} label={`All suppliers (${allSuppliers.length})`} onClick={() => setPicked(allPicked ? [] : [...allSuppliers])} />
                {allSuppliers.map((s) => (
                  <Chip key={s} on={picked.includes(s)} label={s} onClick={() => togglePick(s)} />
                ))}
              </Flex>
            ) : (
              <Flex gap={1} wrap="wrap">
                {(context?.suppliers || []).map((s) => (
                  <Chip key={s} on={supplier === s} label={s} onClick={() => setSupplier(s)} />
                ))}
              </Flex>
            )}
          </FormControl>
          <FormControl isRequired mb={2}>
            <FormLabel fontSize="sm">Retailers</FormLabel>
            <Flex gap={1} wrap="wrap">
              {entitled.map((f) => (
                <Chip key={f} on={!!retailers[f]} label={f.toUpperCase()} onClick={() => setRetailers((p) => ({ ...p, [f]: !p[f] }))} />
              ))}
            </Flex>
          </FormControl>
          <FormControl isRequired mb={4}>
            <FormLabel fontSize="sm">Send to (email)</FormLabel>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="recipient@company.co.za" />
          </FormControl>
          <Button
            type="submit"
            leftIcon={<FaPaperPlane />}
            isLoading={sending}
            isDisabled={(multi ? !picked.length : !supplier) || !email || !entitled.some((f) => retailers[f])}
          >
            Generate & email
          </Button>
        </form>
      </Panel>
    </Box>
  );
};

export default OrgReports;
